import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ProgressBar from './ProgressBar';
import Step1Welcome from '../Steps/Step1Welcome';
import Step2Details from '../Steps/Step2Customer';
import Step3Location from '../Steps/Step3Location';
import Step4Machine from '../Steps/Step4Machine';
import Step5Status from '../Steps/Step5Status';
import Step5FreeRequest from '../Steps/Step5FreeRequest';
import Step6Package from '../Steps/Step6Package';
import Step7Review from '../Steps/Step7Review';
import { filterAvailablePackages } from '../../utils/packageFilters';

import {
  FormStep,
  ServiceRequestFormData,
  OrganizationData
} from '../../types';
import { canAccessFeature } from '../../utils/tierPermissions';
import './MultiStepForm.scss';

interface MultiStepFormProps {
  steps: FormStep[];
  formData: ServiceRequestFormData;
  setFormData: React.Dispatch<React.SetStateAction<ServiceRequestFormData>>;
  organizationData: OrganizationData;
  onSubmit: (data: ServiceRequestFormData) => void;
  /** Fired on mount and whenever the step changes; the hosted header draws its progress ribbon from it. */
  onStepChange?: (index: number, total: number) => void;
}

function MultiStepForm({ steps, formData, setFormData, organizationData, onSubmit, onStepChange }: MultiStepFormProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    onStepChange?.(currentStepIndex, steps.length);
  }, [currentStepIndex, steps.length, onStepChange]);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const stepValidatorRef = useRef<(() => boolean) | null>(null);

  const registerValidation = useCallback((validator: () => boolean) => {
    stepValidatorRef.current = validator;
  }, []);

  const handleNext = async () => {
    let canProceed = true;

    if (stepValidatorRef.current) {
      canProceed = stepValidatorRef.current();
    }

    if (!canProceed) return;

    // Check if we're on Step 2 (Customer Details) and need to create/fetch customer
    const currentStep = steps[currentStepIndex];
    if (currentStep.id === 2 && !formData.customerId) {
      // Call get-or-create-customer before proceeding
      setIsCreatingCustomer(true);
      setCustomerError(null);

      try {
        const { supabase } = await import('../../config/supabaseClient');

        const { data, error } = await supabase.functions.invoke('get-or-create-customer', {
          body: {
            organizationId: organizationData.organization.id,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            mobilePhone: formData.mobilePhone,
            homePhone: formData.homePhone,
            preferredContact: formData.preferredContact,
            smsConsent: formData.smsConsent
          }
        });

        if (error) {
          throw new Error(error.message || 'Failed to create customer record');
        }

        if (data?.success && data.customer_id) {
          // Store customer UUID and address ID in form data
          setFormData(prev => ({
            ...prev,
            customerId: data.customer_id,
            customerAddressId: data.customer_address_id || null,
          }));
          console.log('✅ Customer created/fetched:', data.customer_id, data.is_new ? '(new)' : '(existing)');
        } else {
          throw new Error(data?.error || 'Failed to create customer record');
        }
      } catch (err: any) {
        console.error('❌ Error creating customer:', err);
        setCustomerError(err.message || 'Failed to create customer record');
        setIsCreatingCustomer(false);
        return; // Don't proceed to next step
      } finally {
        setIsCreatingCustomer(false);
      }
    }

    if (currentStepIndex < steps.length - 1) {
      stepValidatorRef.current = null;

      const currentStep = steps[currentStepIndex];
      const hasPackages = canAccessFeature(organizationData.organization.tier, 'packageSelection');

      if (currentStep.id === 5 && hasPackages) {
        const availablePackageIds = filterAvailablePackages(organizationData, formData);

        if (availablePackageIds.length === 0) {
          console.log('⏭️ No packages available, skipping to review step');
          setCurrentStepIndex(currentStepIndex + 2);
          return;
        }
      }

      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      onSubmit(formData);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      stepValidatorRef.current = null;

      const currentStep = steps[currentStepIndex];
      const hasPackages = canAccessFeature(organizationData.organization.tier, 'packageSelection');

      if (currentStep.id === 7 && hasPackages) {
        const availablePackageIds = filterAvailablePackages(organizationData, formData);

        if (availablePackageIds.length === 0) {
          console.log('⏮️ No packages available, skipping back over package step');
          setCurrentStepIndex(currentStepIndex - 2);
          return;
        }
      }

      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const updateFormData = useCallback((newData: Partial<ServiceRequestFormData>) => {
    setFormData(prev => ({ ...prev, ...newData }));
  }, [setFormData]);

  const commonProps = useMemo(() => ({ 
    formData, 
    updateFormData, 
    registerValidation, 
    organizationData 
  }), [formData, updateFormData, registerValidation, organizationData]);

  const currentStepElement = useMemo(() => {
    const step = steps[currentStepIndex];

    if (!organizationData?.organization) {
      return <div>Loading organization details...</div>;
    }

    const hasPackages = canAccessFeature(organizationData.organization.tier, 'packageSelection');

    switch (step.id) {
      case 1:
        return <Step1Welcome {...commonProps} />;
      case 2:
        return <Step2Details {...commonProps} />;
      case 3:
        return <Step3Location {...commonProps} />;
      case 4:
        return <Step4Machine {...commonProps} />;
      case 5:
        return hasPackages ? (
          <Step5Status
            formData={formData}
            updateFormData={updateFormData}
            registerValidation={registerValidation}
            organizationStatusSettings={organizationData.organizationStatusSettings || []}
            organizationData={organizationData}
          />
        ) : (
          <Step5FreeRequest {...commonProps} />
        );
      case 6:
        return <Step6Package {...commonProps} />;
      case 7:
        return <Step7Review {...commonProps} />;
      default:
        return <div>Step Component not found for ID: {step.id}</div>;
    }
  }, [currentStepIndex, steps, commonProps, formData, updateFormData, registerValidation, organizationData]);

  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className="form-card">
      <ProgressBar steps={steps} currentStepIndex={currentStepIndex} />

      {customerError && (
        <div className="error-message summary-error">
          <strong>Error:</strong> {customerError}
        </div>
      )}

      <div className="step-content-wrapper">
         {/* Keyed by step so each step enters with the step-in animation. */}
         <div className="step-content" key={currentStepIndex}>
           {currentStepElement}
         </div>
      </div>

      <div className="step-navigation">
        <button
          onClick={handlePrev}
          disabled={currentStepIndex === 0 || isCreatingCustomer}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={isCreatingCustomer}
          className="btn btn-primary"
        >
          {isCreatingCustomer ? 'Creating Customer...' : isLastStep ? 'Submit Request' : 'Next'}
        </button>
      </div>
    </div>
  );
}

export default MultiStepForm;